import { useEffect } from 'react'
import { createPermissionWatcher } from '@/features/devices/services/permission-watcher'
import { useDevicesStore } from '@/features/devices/store/devices.store'
import { useMeetingSessionStore } from '../store/meeting-session.store'
import { recoverDeviceNow } from '../services/device-permission-recovery'

/**
 * Watch the browser's mic/camera grants for the life of a meeting and drive the
 * no-reload recovery flow.
 *
 * Mounted once inside the meeting (never prejoin — the lobby has its own
 * `MicPermissionDialog`). On a revocation it turns the device off and raises the
 * recovery dialog; on the grant coming back it re-opens the device and restores
 * it, all without a page reload. The device manager's `NotAllowedError` path
 * (via `useDeviceIssues`) is a second way in, for the browsers where the
 * Permissions `change` event is unreliable.
 */
export function usePermissionWatcher(): void {
  useEffect(() => {
    // Recovery state is a devices-store singleton that outlives one meeting (the
    // app is an SPA). Clear it as this meeting starts, so a dialog left unresolved
    // in a previous meeting — or armed by a prejoin capture failure through
    // `useDeviceIssues`, which runs outside the meeting — never pops over a fresh
    // one, and a stale `wasOn` never silently unmutes someone on rejoin.
    useDevicesStore.getState().endPermissionRecovery('audio')
    useDevicesStore.getState().endPermissionRecovery('video')

    const watcher = createPermissionWatcher({
      onChange: (kind, state, previous) => {
        const devices = useDevicesStore.getState()
        // Keep the store's per-kind verdict in step with the browser. On
        // 'denied' this also lowers the lobby toggle; the meeting toggle is
        // handled below.
        devices.setDevicePermission(kind, state)

        // Revoked while we had access — granted -> denied, or granted -> prompt
        // (Reset to "Ask"). Turn the device off, remember it was on, and raise
        // the dialog.
        if (previous === 'granted' && state !== 'granted') {
          const session = useMeetingSessionStore.getState()
          const wasOn = kind === 'audio' ? session.micOn : session.camOn
          if (kind === 'audio') session.setMicOn(false)
          else session.setCamOn(false)
          devices.beginPermissionRecovery(kind, wasOn)
          return
        }

        // The grant came back. Re-open the device and restore it with no reload,
        // but only if we armed recovery for it — ignore an unrelated first grant.
        if (state === 'granted' && devices.permissionRecovery[kind]) {
          void recoverDeviceNow(kind)
        }
      },
    })
    return () => {
      watcher.dispose()
      // Leaving the meeting ends recovery too — otherwise the dialog/arming
      // would carry into the next meeting in the same page session.
      const devices = useDevicesStore.getState()
      devices.endPermissionRecovery('audio')
      devices.endPermissionRecovery('video')
    }
  }, [])
}
